import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { PageTitle, Panel, Loading, ErrorState, Pill } from "../../components/app/Kit";
import { StatusChip } from "../../components/site/Primitives";
import { TIMEZONES } from "../../lib/timezones";
import { useAuth } from "../../context/AuthContext";

const SECTIONS = [
  "Business Profile", "Users", "Roles", "Calendar", "Voice", "Messaging", "Email",
  "Payments", "AI Agents", "Automation", "Notifications", "Security", "Integrations", "Billing",
];

const INTEGRATION_SECTION = {
  Calendar: "google_calendar", Voice: "vapi", Messaging: "twilio",
  Email: "email", Payments: "stripe", Integrations: null,
};

export default function Settings() {
  const { org, setOrg, user } = useAuth();
  const [section, setSection] = useState("Business Profile");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");

  const load = async () => {
    setState("loading");
    try {
      const [{ data: o }, { data: ints }] = await Promise.all([
        api.get("/organization"),
        api.get("/integrations"),
      ]);
      setForm(o);
      setIntegrations(ints);
      setState("ready");
    } catch (e) {
      setError(apiError(e));
      setState("error");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/organization", {
        name: form.name, industry: form.industry, timezone: form.timezone,
        location: form.location, business_hours: form.business_hours,
        contact_email: form.contact_email, demo_mode: form.demo_mode,
      });
      setForm(data);
      setOrg(data);
      toast.success("Settings saved.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (state === "loading") return <Loading rows={8} testId="settings-loading" />;
  if (state === "error") return <ErrorState message={error} onRetry={load} testId="settings-error" />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="settings-page">
      <PageTitle testId="settings-title" title="Settings" sub="Business profile, providers, security and billing configuration." />

      <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
        <nav className="scrollbar-thin flex gap-1.5 overflow-x-auto lg:sticky lg:top-20 lg:grid lg:self-start lg:overflow-visible">
          {SECTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSection(s)}
              data-testid={`settings-section-${s.toLowerCase().replace(/\s+/g, "-")}`}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-left text-[12px] transition-colors duration-200 ${
                section === s ? "bg-elv-green/[0.09] text-elv-green" : "text-elv-dim hover:text-elv-text"
              }`}
            >
              {s}
            </button>
          ))}
        </nav>

        <div className="grid gap-4">
          {section === "Business Profile" ? (
            <Panel testId="settings-business-profile" title="Business profile">
              <div className="grid gap-4 sm:grid-cols-2">
                {[
                  ["name", "Business name"], ["industry", "Industry"],
                  ["location", "Location"], ["business_hours", "Business hours"], ["contact_email", "Contact email"],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label className="text-[10px] uppercase tracking-[0.18em] text-elv-dim">{label}</label>
                    <input
                      value={form[key] || ""}
                      onChange={set(key)}
                      data-testid={`settings-input-${key}`}
                      className="mt-2 w-full rounded-md border border-elv-line bg-white/[0.02] px-3 py-2 text-xs text-elv-text outline-none focus:border-elv-green/40"
                    />
                  </div>
                ))}
                <div>
                  <label className="text-[10px] uppercase tracking-[0.18em] text-elv-dim">Timezone</label>
                  <select
                    value={form.timezone || "UTC"}
                    onChange={set("timezone")}
                    data-testid="settings-input-timezone"
                    className="mt-2 w-full rounded-md border border-elv-line bg-white/[0.02] px-3 py-2 text-xs text-elv-text outline-none focus:border-elv-green/40"
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz} value={tz} className="bg-[#0A0F14]">{tz}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-[10px] text-elv-dim">
                    Used for weekly AI reports (Monday 08:00 local), reminders and automation timing.
                  </p>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  data-testid="settings-save"
                  className="inline-flex items-center gap-2 rounded-md bg-elv-green px-4 py-2 text-xs font-semibold text-[#04120A] disabled:opacity-60"
                >
                  {saving ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  Save changes
                </button>
                <label className="flex items-center gap-2 text-xs text-elv-dim">
                  <input
                    type="checkbox"
                    checked={!!form.demo_mode}
                    onChange={(e) => setForm((f) => ({ ...f, demo_mode: e.target.checked }))}
                    data-testid="settings-demo-mode"
                    className="h-3.5 w-3.5 accent-[#00FF66]"
                  />
                  Demo mode (shows the demo banner and labels data as fictional)
                </label>
              </div>
            </Panel>
          ) : null}

          {section === "Users" || section === "Roles" ? (
            <Panel testId="settings-users" title={section}>
              <p className="text-xs text-elv-dim">
                Users and roles are managed on the Team page, where you can see workload alongside each role.
              </p>
              <Link
                to="/app/team"
                data-testid="settings-open-team"
                className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-elv-green hover:text-elv-cyan"
              >
                Open team management <Icons.ArrowUpRight className="h-3 w-3" />
              </Link>
            </Panel>
          ) : null}

          {INTEGRATION_SECTION[section] !== undefined ? (
            <Panel testId={`settings-provider-${section.toLowerCase()}`} title={`${section} provider`}>
              {(() => {
                const key = INTEGRATION_SECTION[section];
                const list = key ? integrations.filter((i) => i.key === key) : integrations;
                return (
                  <div className="grid gap-2">
                    {list.map((i) => (
                      <div key={i.key} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
                        <div>
                          <p className="text-xs text-elv-text">{i.name}</p>
                          <p className="mt-1 text-[11px] text-elv-dim">{i.description}</p>
                        </div>
                        <StatusChip status={i.status} />
                      </div>
                    ))}
                    <Link
                      to="/app/integrations"
                      data-testid="settings-open-integrations"
                      className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-elv-green hover:text-elv-cyan"
                    >
                      Manage connections <Icons.ArrowUpRight className="h-3 w-3" />
                    </Link>
                  </div>
                );
              })()}
            </Panel>
          ) : null}

          {section === "AI Agents" ? (
            <Panel testId="settings-ai-agents" title="AI agents">
              <p className="text-xs text-elv-dim">
                Agent instructions, channels, tools and escalation rules are configured per agent.
              </p>
              <Link to="/app/ai-agents" data-testid="settings-open-agents" className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-elv-green hover:text-elv-cyan">
                Open AI agents <Icons.ArrowUpRight className="h-3 w-3" />
              </Link>
            </Panel>
          ) : null}

          {section === "Automation" ? (
            <Panel testId="settings-automation" title="Automation preferences">
              <p className="text-xs text-elv-dim">
                Enable or pause individual workflows and review their execution history.
              </p>
              <Link to="/app/automations" data-testid="settings-open-automations" className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-elv-green hover:text-elv-cyan">
                Open automations <Icons.ArrowUpRight className="h-3 w-3" />
              </Link>
            </Panel>
          ) : null}

          {section === "Notifications" ? (
            <Panel testId="settings-notifications" title="Notification types">
              <div className="grid gap-2">
                {["New lead", "Appointment booked", "Appointment cancelled", "Payment received", "AI escalation", "Automation failure", "Integration failure", "Follow-up due"].map((n) => (
                  <div key={n} className="flex items-center justify-between rounded-md border border-white/[0.06] bg-white/[0.02] p-3 text-xs">
                    <span className="text-elv-text">{n}</span>
                    <Pill tone="green">In-app</Pill>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-[11px] text-elv-dim">
                Email and SMS notification delivery requires a connected email or messaging provider.
              </p>
            </Panel>
          ) : null}

          {section === "Security" ? (
            <Panel testId="settings-security" title="Security">
              <div className="grid gap-2">
                {[
                  ["Authentication", "Email + password with hashed credentials, plus Google sign-in."],
                  ["Session handling", "httpOnly cookies with short-lived access tokens and refresh rotation."],
                  ["Authorisation", "Role-based access with organisation isolation on every record."],
                  ["Data isolation", `All records in this workspace are scoped to organisation ${org?.id}.`],
                  ["Secrets", "Provider credentials are stored server-side and never sent to the browser."],
                  ["Brute force protection", "Repeated failed sign-ins are rate limited and temporarily locked."],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
                    <p className="text-xs font-medium text-elv-text">{k}</p>
                    <p className="mt-1.5 text-[11px] text-elv-dim">{v}</p>
                  </div>
                ))}
              </div>
              <p className="mt-4 rounded-md border border-amber-300/25 bg-amber-300/[0.05] p-3 text-[11px] text-amber-200">
                If you handle health, legal or other regulated data, additional infrastructure and agreements are
                required before production use. No compliance certification is claimed.
              </p>
            </Panel>
          ) : null}

          {section === "Billing" ? (
            <Panel testId="settings-billing" title="Billing">
              <div className="grid gap-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-elv-dim">Plan</span>
                  <span className="uppercase text-elv-text">{org?.plan}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-elv-dim">Status</span>
                  <Pill tone={org?.subscription_status === "active" ? "green" : "amber"}>{org?.subscription_status}</Pill>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-elv-dim">Account owner</span>
                  <span className="text-elv-text">{user?.email}</span>
                </div>
                <Link to="/app/payments" data-testid="settings-open-payments" className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-elv-green hover:text-elv-cyan">
                  Open payments <Icons.ArrowUpRight className="h-3 w-3" />
                </Link>
              </div>
            </Panel>
          ) : null}
        </div>
      </div>
    </div>
  );
}
